import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

interface DealerProduct {
  product_id: string;
  product_name: string;
  product_code: string;
  brand: string;
  barcode: string;
  total_quantity: number;
  last_price: number;
  purchase_price: number;
  current_stock: number;
  last_added_at: string | null;
}

interface Props {
  currentDealerId: string;
  dealerId: string;
  dealerName: string;
  onDataChanged: () => void;
}

export default function DealerProductsTab({ currentDealerId, dealerId, dealerName, onDataChanged }: Props) {
  const [products, setProducts] = useState<DealerProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState<string | null>(null);
  const [removeQty, setRemoveQty] = useState(1);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const { data: salesData } = await supabase
        .from("sales")
        .select("id, created_at")
        .eq("current_dealer_id", currentDealerId)
        .eq("dealer_id", dealerId);

      if (!salesData || salesData.length === 0) {
        setProducts([]);
        return;
      }

      const saleIds = salesData.map((s) => s.id);

      const { data: itemsData } = await supabase
        .from("sale_items")
        .select("product_id, quantity, unit_price, purchase_price, sale_id")
        .in("sale_id", saleIds);

      if (!itemsData || itemsData.length === 0) {
        setProducts([]);
        return;
      }

      const productMap = new Map<string, { total_quantity: number; last_price: number; purchase_price: number; last_date: string }>();
      itemsData.forEach((item) => {
        const existing = productMap.get(item.product_id);
        if (existing) {
          existing.total_quantity += Number(item.quantity);
          existing.last_price = Number(item.unit_price);
          existing.purchase_price = Number(item.purchase_price);
        } else {
          productMap.set(item.product_id, {
            total_quantity: Number(item.quantity),
            last_price: Number(item.unit_price),
            purchase_price: Number(item.purchase_price),
            last_date: "",
          });
        }
      });

      const saleDateMap = new Map<string, string>();
      (salesData || []).forEach((s) => { saleDateMap.set(s.id, s.created_at); });

      // Map each sale_item back to its sale date and track latest per product
      itemsData.forEach((item: any) => {
        const saleDate = saleDateMap.get(item.sale_id);
        if (saleDate) {
          const existing = productMap.get(item.product_id);
          if (existing && (!existing.last_date || saleDate > existing.last_date)) {
            existing.last_date = saleDate;
          }
        }
      });

      const productIds = [...productMap.keys()];
      const { data: prodData } = await supabase
        .from("products")
        .select("id, name, product_code, brand, barcode, stock")
        .in("id", productIds)
        .eq("dealer_id", dealerId);

      const prodInfoMap = new Map<string, { name: string; product_code: string; brand: string; barcode: string; stock: number }>();
      (prodData || []).forEach((p) => prodInfoMap.set(p.id, { name: p.name, product_code: p.product_code || "", brand: p.brand || "", barcode: p.barcode || "", stock: p.stock || 0 }));

      const result: DealerProduct[] = productIds.map((pid) => {
        const info = prodInfoMap.get(pid);
        const qty = productMap.get(pid)!;
        return {
          product_id: pid,
          product_name: info?.name || "Bilinmeyen Ürün",
          product_code: info?.product_code || "",
          brand: info?.brand || "",
          barcode: info?.barcode || "",
          total_quantity: qty.total_quantity,
          last_price: qty.last_price,
          purchase_price: qty.purchase_price,
          current_stock: info?.stock || 0,
          last_added_at: qty.last_date || null,
        };
      });

      result.sort((a, b) => b.total_quantity - a.total_quantity);
      setProducts(result);
    } catch (_err) {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [currentDealerId, dealerId]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleRemove = async (product: DealerProduct) => {
    const qty = removeQty;
    if (qty <= 0 || qty > product.total_quantity) return;

    setRemoving(product.product_id);
    try {
      const refundAmount = product.last_price * qty;

      const { data: prodRow } = await supabase
        .from("products")
        .select("stock")
        .eq("id", product.product_id)
        .single();

      const currentStock = Number(prodRow?.stock || 0);
      const newStock = currentStock + qty;

      await supabase.from("products").update({ stock: newStock }).eq("id", product.product_id);

      const { data: dealerRow } = await supabase
        .from("current_dealers")
        .select("balance")
        .eq("id", currentDealerId)
        .single();

      const currentBalance = Number(dealerRow?.balance || 0);
      const newBalance = currentBalance - refundAmount;

      await supabase.from("current_dealers").update({ balance: newBalance }).eq("id", currentDealerId);

      await supabase.from("payment_notifications").insert({
        dealer_id: dealerId,
        current_dealer_id: currentDealerId,
        amount: refundAmount,
        status: "completed",
        note: `${product.product_name} ürün iadesi - ${qty} adet (birim: $${product.last_price.toLocaleString("en-US", { minimumFractionDigits: 2 })})`,
      });

      setShowRemoveConfirm(null);
      setRemoveQty(1);
      onDataChanged();
      fetchProducts();
    } catch (_err) {
      // silently fail
    } finally {
      setRemoving(null);
    }
  };

  const totalValue = products.reduce((sum, p) => sum + p.last_price * p.total_quantity, 0);

  return (
    <div>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-12 text-foreground-400">
          <i className="ri-box-3-line text-3xl block mb-2"></i>
          <p className="text-sm">Bu bayiye henüz ürün satışı yapılmamış</p>
          <p className="text-xs mt-1">Satış terminalinden bu bayiye ürün ekleyebilirsiniz</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-foreground-500">
              {products.length} farklı ürün &middot; Toplam değer: <strong className="text-foreground-900">${totalValue.toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong>
            </p>
          </div>
          <div className="space-y-2">
            {products.map((product) => (
              <div key={product.product_id} className="bg-background-100 rounded-xl border border-background-200/70 p-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-background-50 border border-background-200/70 flex items-center justify-center shrink-0">
                    <i className="ri-box-3-line text-foreground-400"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-900 truncate">{product.product_name}</p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                          {product.brand && <span className="text-[10px] text-foreground-400 bg-background-50 px-1.5 py-0.5 rounded">{product.brand}</span>}
                          {product.product_code && <span className="text-[10px] text-foreground-400 font-mono">{product.product_code}</span>}
                          {product.barcode && <span className="text-[10px] text-foreground-400 font-mono">{product.barcode}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-2 gap-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs text-foreground-500">
                          Bayide: <strong className="text-foreground-900">{product.total_quantity}</strong> adet
                        </span>
                        <span className="text-xs text-foreground-400">
                          Depoda: {product.current_stock} adet
                        </span>
                        <span className="text-xs font-medium text-primary-600">
                          ${product.last_price.toLocaleString("en-US", { minimumFractionDigits: 2 })}/adet
                        </span>
                        {product.last_added_at && (
                          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
                            <i className="ri-time-line text-[10px]"></i>
                            {new Date(product.last_added_at).toLocaleString("tr-TR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        {showRemoveConfirm === product.product_id ? (
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number"
                              min={1}
                              max={product.total_quantity}
                              value={removeQty}
                              onChange={(e) => setRemoveQty(Math.max(1, Math.min(product.total_quantity, Number(e.target.value) || 1)))}
                              className="w-14 px-2 py-1 bg-background-50 border border-background-200/70 rounded-md text-xs text-foreground-950 text-center focus:outline-none focus:ring-2 focus:ring-accent-400"
                            />
                            <button
                              onClick={() => handleRemove(product)}
                              disabled={removing === product.product_id}
                              className="px-2 py-1 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                            >
                              {removing === product.product_id ? <i className="ri-loader-4-line animate-spin"></i> : "Onayla"}
                            </button>
                            <button
                              onClick={() => { setShowRemoveConfirm(null); setRemoveQty(1); }}
                              className="px-2 py-1 bg-background-50 border border-background-200/70 text-foreground-500 rounded-md text-xs cursor-pointer whitespace-nowrap hover:bg-background-200/70"
                            >
                              İptal
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setShowRemoveConfirm(product.product_id); setRemoveQty(1); }}
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-accent-100 hover:bg-accent-200/70 text-accent-700 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-arrow-go-back-line text-xs"></i> Ürün Çıkar
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}